import type { BuildingHours } from '@hub-wifi/shared'
import { nowHmInTz, todayYmdInTz } from '../../lib/calendar-time'

export type OpenStatus =
  | { state: 'open'; until: string }
  | { state: 'opensAt'; at: string }
  | { state: 'closed' }

/** Day of week (0 = Sunday) of a `YYYY-MM-DD` wall-clock date. */
function dayOfWeek(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay()
}

/** Today's entry from the weekly hours (building timezone), if any. */
export function hoursForToday(
  hours: BuildingHours[],
  timeZone: string,
  now: Date,
): BuildingHours | undefined {
  const dow = dayOfWeek(todayYmdInTz(timeZone, now))
  return hours.find((h) => h.dayOfWeek === dow)
}

/**
 * Open/closed right now from the building's weekly hours, in the building
 * timezone. Returns `null` when it can't be told reliably (no hours for
 * today, or hours crossing midnight), so the caller shows just the date.
 */
export function openStatus(hours: BuildingHours[], timeZone: string, now: Date): OpenStatus | null {
  const today = hoursForToday(hours, timeZone, now)
  if (!today) return null
  if (today.isClosed) return { state: 'closed' }
  if (!today.openTime || !today.closeTime || today.closeTime <= today.openTime) return null
  const nowHm = nowHmInTz(timeZone, now)
  if (nowHm < today.openTime) return { state: 'opensAt', at: today.openTime }
  if (nowHm < today.closeTime) return { state: 'open', until: today.closeTime }
  return { state: 'closed' }
}

export type DayPeriod = 'morning' | 'afternoon' | 'evening'

/** Greeting period from the building's wall clock. */
export function dayPeriod(timeZone: string, now: Date): DayPeriod {
  const hour = Number(nowHmInTz(timeZone, now).slice(0, 2))
  if (hour >= 5 && hour < 12) return 'morning'
  if (hour >= 12 && hour < 18) return 'afternoon'
  return 'evening'
}
